import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQueries } from "@tanstack/react-query";
import { useStore } from "../lib/storeContext";
import { getJob } from "../lib/apiClient";
import { RenderThumb } from "../components/RenderThumb";
import { SaveHeart } from "../components/SaveHeart";
import { Lightbox } from "../components/Lightbox";
import type { LightboxSlide } from "../components/Lightbox";
import { Breadcrumbs, EmptyState, Spinner } from "./ui";
import { cycleNeedsJobFallback, resolveConceptGroups } from "../lib/concepts";
import { formatDateTime } from "../lib/util";
import type { CyclePayload, Job, RoomPayload, SessionPayload } from "../lib/types";

export function SessionGalleryView() {
  const { roomId = "", sessionId = "" } = useParams();
  const navigate = useNavigate();
  const { store, rev, loading } = useStore();
  const [lightbox, setLightbox] = useState<number | null>(null);
  void rev;

  const session = store.get<SessionPayload>("SESSION", sessionId);
  const cycles = store.cycles(sessionId);

  // Cycles whose finished renders never synced → recover them from the backend job.
  const fallbackJobIds = useMemo(
    () =>
      [...new Set(
        cycles
          .filter((c) => cycleNeedsJobFallback(c.payload as CyclePayload))
          .map((c) => (c.payload as CyclePayload).jobId)
          .filter((j): j is string => !!j),
      )],
    [cycles],
  );
  const jobQueries = useQueries({
    queries: fallbackJobIds.map((jid) => ({
      queryKey: ["job", jid],
      queryFn: () => getJob(jid),
      staleTime: 5 * 60 * 1000,
      retry: 1,
    })),
  });
  const jobMap = useMemo(() => {
    const m = new Map<string, Job>();
    fallbackJobIds.forEach((jid, i) => {
      const data = jobQueries[i]?.data;
      if (data) m.set(jid, data);
    });
    return m;
  }, [fallbackJobIds, jobQueries]);

  const room = store.get<RoomPayload>("ROOM", roomId);
  const jobsLoading = jobQueries.some((q) => q.isLoading);
  // Viewers can't start refinement makeovers (MakeoverView also route-guards this).
  const isViewer = store.roleFor(store.projectIdOf("ROOM", roomId)) === "viewer";

  const { slides, cards } = useMemo(() => {
    const slides: LightboxSlide[] = [];
    const cards: {
      round: number;
      feedback?: string | null;
      groups: ReturnType<typeof resolveConceptGroups>;
    }[] = [];
    for (const cycle of cycles) {
      const cp = cycle.payload as CyclePayload;
      const job = cp.jobId ? jobMap.get(cp.jobId) : undefined;
      const groups = resolveConceptGroups(cp, store, job);
      for (const g of groups) {
        for (const r of g.renders) {
          slides.push({ s3Key: r.s3Key, url: r.url, caption: g.name, beforeS3Key: r.beforeS3Key });
        }
      }
      cards.push({ round: cp.index ?? 0, feedback: cp.feedback, groups });
    }
    return { slides, cards };
  }, [cycles, store, jobMap, rev]);

  if (loading) return <Spinner />;
  if (!session) return <EmptyState title="Makeover not found" icon="🔍" />;

  const totalRenders = cards.reduce((n, c) => n + c.groups.reduce((m, g) => m + g.renders.length, 0), 0);
  let slideCursor = 0;

  return (
    <div className="space-y-6">
      <Breadcrumbs
        items={[
          { label: "Projects", to: "/" },
          { label: room?.payload.name ?? "Room", to: `/rooms/${roomId}` },
          { label: "Makeover" },
        ]}
      />
      <div>
        <h1 className="text-2xl font-bold text-ink">{session.payload.goal || "Makeover"}</h1>
        <p className="text-sm text-muted">{formatDateTime(session.payload.createdAt)}</p>
      </div>

      {totalRenders === 0 &&
        (jobsLoading ? (
          <Spinner label="Loading ideas…" />
        ) : (
          <EmptyState title="No ideas were saved from this makeover" icon="🎨" />
        ))}

      {cards.map((card, ci) => (
        <section key={ci} className="space-y-4">
          {cards.length > 1 && (
            <h2 className="text-lg font-semibold text-ink">{card.round === 0 ? "First ideas" : `Round ${card.round + 1}`}</h2>
          )}
          {card.feedback && (
            <p className="rounded-card bg-cream px-4 py-2 text-sm italic text-ink/70">“{card.feedback}”</p>
          )}
          {card.groups.map((g) => (
            <div key={g.conceptIndex}>
              <div className="mb-2 flex items-center gap-2">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-warm text-xs font-semibold text-white">
                  {g.conceptIndex + 1}
                </span>
                <h3 className="font-semibold text-ink">{g.name}</h3>
                {ci === cards.length - 1 && !isViewer && (
                  <button
                    className="btn-ghost ml-auto px-3 py-1 text-xs"
                    onClick={() => navigate(`/rooms/${roomId}/makeover?refineSession=${sessionId}&concept=${g.conceptIndex}`)}
                  >
                    ✨ Build on this
                  </button>
                )}
              </div>
              {g.brief && <p className="mb-2 max-w-3xl text-sm text-muted">{g.brief}</p>}
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                {g.renders.map((r) => {
                  const myIndex = slideCursor++;
                  const asset = r.fileName ? store.assetForLocalPath(`photos/${r.fileName}`) : undefined;
                  return (
                    <div key={r.key} className="relative">
                      <RenderThumb
                        s3Key={r.s3Key}
                        url={r.url}
                        alt={g.name}
                        className="aspect-square rounded-card shadow-card"
                        onClick={() => setLightbox(myIndex)}
                      />
                      <div className="absolute right-2 top-2">
                        <SaveHeart
                          roomId={roomId}
                          goal={`${session.payload.goal} — ${g.name}`}
                          renderFileName={r.fileName}
                          renderAsset={asset}
                          renderUrl={r.url}
                          sourceId={r.url ? r.key : undefined}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </section>
      ))}

      {lightbox != null && (
        <Lightbox slides={slides} index={lightbox} onClose={() => setLightbox(null)} onIndexChange={setLightbox} />
      )}
    </div>
  );
}
